import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StatusBar, View, Text, StyleSheet, Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context';
import { DarkTheme, DefaultTheme, NavigationContainer, type NavigationContainerRef } from '@react-navigation/native';
import type { RootStackParamList } from './navigation/types';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import {
  setAuthApiConfig,
  authApi,
  completeSession,
  getAuthStore,
  resetPostLoginGrace,
  setLoginJustHappened,
  useAuthStore,
} from './shared/auth';
import { loadAppConfig, getAllergyPickerMode } from './data/domains/config';
import { usePreferencesStore, type ProfileResponseLike, prefetchCombinedPreferencesCatalog } from './data/domains/preferences';
import { resolveDevEnvironment, resolveDevInitialScreens, loadSubscriptionMenuProbePreference, loadDevApiCallHistoryFromStorage } from './exDev';
import { useThemeStore, ThemeService } from './shared/theme';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  notifyNavigationRootReady,
  resetRootNavigationForAuth,
  setNavigationRootRef,
} from './navigation/navigationRootRef';
import {
  I18nProvider,
  setLocale,
  LOCALE_STORAGE_KEY,
  tryRestartApp,
} from './shared/i18n';
import { HealthInformationCmsProvider } from './healthInformation/components/HealthInformationCmsContext';
import { navigationLinking } from './navigation/linking';
import { AuthSheetProvider } from './shared/auth/authSheetContext';
import { AppInitializer, AppAuthSheetHost, AppSplash, AppToastRoot } from './lib/appShell';
import { ensureLaunchBannersReady } from './lib/appBootstrap';
import { resetSplashLaunchGate, setSplashCovering, setSplashPageReady } from './lib/splashGate';
import { useSplashPageReady } from './lib/useSplashCovering';
import { appLogger } from './shared/utils/logger';
import { isWebRuntime } from './shared/platform/runtimeTarget';
import { subscribeFcmForegroundNotifications, subscribeFcmTokenRefresh } from './platform/notifications/fcmDeviceToken';
import { installFcmBackendSyncLifecycle, scheduleFcmBackendSync } from './platform/notifications/fcmBackendSync';
import moment from 'moment';
import { installIssueReportHandlers } from './platform/support/installIssueReportHandlers';

const IS_WEB = isWebRuntime();

const styles = StyleSheet.create({
  root: { flex: 1, height: '100%' as unknown as number },
  /** Lets `AuthSheetHost` + `ConfigurableOverlay` use `absoluteFill` over the full window without `Modal`. */
  navShell: { flex: 1, position: 'relative', height: '100%' as unknown as number },
  navMain: { flex: 1, minHeight: 0, height: '100%' as unknown as number },
  webBoot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0b1220',
    height: '100%' as unknown as number,
  },
  webBootText: {
    color: '#e2e8f0',
    fontSize: 16,
  },
});

const App = () => {
  const appearance = useThemeStore(state => state.appearance);
  const isThemeInitialized = useThemeStore(state => state.isInitialized);
  const colors = useThemeStore(state => state.colors);
  const is_authenticated = useAuthStore(state => state.isAuthenticated);
  const nav_ref = useRef<NavigationContainerRef<RootStackParamList>>(null);
  /** Config + theme + auth (+ guest banners/media) finished — safe to mount the app under splash. */
  const [shell_ready, set_shell_ready] = useState(false);
  /** Navigation tree has reported ready — first screen can paint. */
  const [ui_ready, set_ui_ready] = useState(false);
  /** First screen reported content ready (banner painted when present). */
  const page_ready = useSplashPageReady();
  /** PWA: skip bootstrap splash so Auth/Main can paint immediately. */
  const [splash_visible, set_splash_visible] = useState(!IS_WEB);

  useEffect(() => {
    if (IS_WEB) {
      // Web skips splash; keep gate open so nothing waits on covering/page_ready.
      setSplashCovering(false);
      setSplashPageReady(true);
      return;
    }
    resetSplashLaunchGate();
  }, []);

  useEffect(() => {
    // Splash owns covering=true; only force-clear when the overlay is gone (safety net).
    if (!splash_visible) {
      setSplashCovering(false);
    }
  }, [splash_visible]);

  useEffect(() => {
    if (is_authenticated) {
      scheduleFcmBackendSync('auth-state');
      const customer_id = getAuthStore().user?.id;
      if (getAllergyPickerMode() === 'combined' && typeof customer_id === 'number') {
        prefetchCombinedPreferencesCatalog(queryClient, customer_id).catch(() => {});
      }
    }
  }, [is_authenticated]);

  const onNavigationReady = useCallback(() => {
    notifyNavigationRootReady();
    // Defer one frame so the initial route can commit before splash reverse.
    requestAnimationFrame(() => {
      set_ui_ready(true);
      if (IS_WEB) {
        setSplashPageReady(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!IS_WEB) {
      return;
    }
    const w = globalThis as typeof globalThis & {
      __EATAAMI_GET_NAV__?: () => NavigationContainerRef<RootStackParamList> | null;
      /** PWA smoke only — mutates the live auth store (same module instance as the app). */
      __EATAAMI_SOFT_AUTH__?: (enable?: boolean) => { isAuthenticated: boolean };
      __EATAAMI_SOFT_AUTH_GRACE_IV__?: ReturnType<typeof setInterval>;
    };
    w.__EATAAMI_GET_NAV__ = () => nav_ref.current;
    w.__EATAAMI_SOFT_AUTH__ = (enable = true) => {
      if (w.__EATAAMI_SOFT_AUTH_GRACE_IV__) {
        clearInterval(w.__EATAAMI_SOFT_AUTH_GRACE_IV__);
        delete w.__EATAAMI_SOFT_AUTH_GRACE_IV__;
      }
      if (enable) {
        completeSession({
          id: 1,
          name: 'PWA Test',
          mail: 'pwa@test.local',
          phone: '50000000',
          session_token: 'pwa-smoke-token',
          is_verified: true,
        });
        // Fake smoke token triggers API 401s; keep post-login grace so session is not cleared mid-test.
        w.__EATAAMI_SOFT_AUTH_GRACE_IV__ = setInterval(() => setLoginJustHappened(), 2000);
      } else {
        resetPostLoginGrace();
        getAuthStore().clearUser();
        resetRootNavigationForAuth(false);
      }
      return { isAuthenticated: getAuthStore().isAuthenticated };
    };
    return () => {
      if (w.__EATAAMI_SOFT_AUTH_GRACE_IV__) {
        clearInterval(w.__EATAAMI_SOFT_AUTH_GRACE_IV__);
        delete w.__EATAAMI_SOFT_AUTH_GRACE_IV__;
      }
      delete w.__EATAAMI_GET_NAV__;
      delete w.__EATAAMI_SOFT_AUTH__;
    };
  }, []);

  useEffect(() => {
    installIssueReportHandlers();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const prepareApp = async () => {
      const mark_ready = () => {
        if (!cancelled) {
          set_shell_ready(true);
        }
      };
      try {
        const savedLocale = await AsyncStorage.getItem(LOCALE_STORAGE_KEY);
        const locale = savedLocale === 'ar' || savedLocale === 'en' ? savedLocale : 'en';
        const { did_force_rtl } = setLocale(locale);
        if (did_force_rtl && tryRestartApp()) {
          return;
        }
        try {
          await import('moment/locale/ar');
        } catch {
          // ar locale not available
        }
        try {
          moment.locale(locale === 'ar' ? 'ar' : 'en');
        } catch {
          moment.locale('en');
        }
        setAuthApiConfig(queryClient);
        await resolveDevEnvironment();
        await resolveDevInitialScreens();
        await loadSubscriptionMenuProbePreference();
        await loadDevApiCallHistoryFromStorage();

        // Web: don't block first paint on slow config/banners — mount Auth/Main ASAP.
        if (IS_WEB) {
          try {
            await Promise.race([
              loadAppConfig(),
              new Promise<void>(resolve => setTimeout(resolve, 2500)),
            ]);
          } catch {
            // continue with defaults
          }
          try {
            await Promise.race([
              ThemeService.initialize(),
              new Promise<void>(resolve => setTimeout(resolve, 1500)),
            ]);
          } catch {
            // fall through
          }
          if (!useThemeStore.getState().isInitialized) {
            useThemeStore.getState().setInitialized(true);
          }
          await authApi.loadStoredUser().catch(() => {});
          usePreferencesStore.getState().loadFromProfile({
            customer: getAuthStore().user,
          } as unknown as ProfileResponseLike);
          mark_ready();
          // Warm banners in background (classic MainScreen uses them).
          void ensureLaunchBannersReady(queryClient, getAuthStore().isAuthenticated);
          return;
        }

        await loadAppConfig();
        await ThemeService.initialize();
        await authApi.loadStoredUser();
        usePreferencesStore.getState().loadFromProfile({
          customer: getAuthStore().user,
        } as unknown as ProfileResponseLike);
        const launch_customer_id = getAuthStore().user?.id;
        if (getAllergyPickerMode() === 'combined' && typeof launch_customer_id === 'number') {
          prefetchCombinedPreferencesCatalog(queryClient, launch_customer_id).catch(() => {});
        }
        await ensureLaunchBannersReady(queryClient, getAuthStore().isAuthenticated);
        mark_ready();
      } catch (error) {
        appLogger.error('Initialization failed', { error: String(error) });
        if (!useThemeStore.getState().isInitialized) {
          useThemeStore.getState().setInitialized(true);
        }
        mark_ready();
      }
    };
    prepareApp();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unsubscribe_refresh = subscribeFcmTokenRefresh(token => {
      if (typeof token === 'string' && token.length > 0) {
        scheduleFcmBackendSync('token-refresh');
      }
    });
    const unsubscribe_foreground = subscribeFcmForegroundNotifications();
    const uninstall_sync = installFcmBackendSyncLifecycle();
    return () => {
      unsubscribe_refresh();
      unsubscribe_foreground();
      uninstall_sync();
    };
  }, []);

  const navigation_theme = useMemo(() => {
    const base = appearance === 'dark' ? DarkTheme : DefaultTheme;
    return {
      ...base,
      dark: appearance === 'dark',
      colors: {
        ...base.colors,
        primary: colors.primary,
        background: colors.background,
        card: colors.surface,
        text: colors.text,
        border: colors.border,
        notification: colors.primary,
      },
    };
  }, [appearance, colors.background, colors.border, colors.primary, colors.surface, colors.text]);

  const can_dismiss_splash =
    shell_ready && ui_ready && page_ready && isThemeInitialized && !!colors;

  return (
    <GestureHandlerRootView style={styles.root}>
      <QueryClientProvider client={queryClient}>
        <SafeAreaProvider initialMetrics={IS_WEB ? initialWindowMetrics : undefined}>
          {/*
            Mount I18nProvider only after prepareApp has applied AsyncStorage locale.
            Otherwise context.locale stays `en` while module locale is already `ar`, and the
            language toggle cannot switch back to English (setLocale('ar') no-ops).
          */}
          {shell_ready ? (
            <I18nProvider>
              <HealthInformationCmsProvider>
                {Platform.OS === 'android' ? (
                  <StatusBar barStyle={appearance === 'dark' ? 'light-content' : 'dark-content'} />
                ) : null}
                <AuthSheetProvider>
                  <NavigationContainer<RootStackParamList>
                    ref={ref => {
                      nav_ref.current = ref;
                      setNavigationRootRef(ref);
                    }}
                    onReady={onNavigationReady}
                    // Browser URL `/` must not deep-link into MainTabs (auth has no such route).
                    linking={IS_WEB ? undefined : navigationLinking}
                    theme={navigation_theme}>
                    <View style={styles.navShell}>
                      <View style={styles.navMain}>
                        <AppInitializer />
                      </View>
                      <AppAuthSheetHost />
                    </View>
                  </NavigationContainer>
                </AuthSheetProvider>
                <AppToastRoot />

                {splash_visible && !IS_WEB ? (
                  <AppSplash
                    can_dismiss={can_dismiss_splash}
                    on_exit_complete={() => set_splash_visible(false)}
                  />
                ) : null}
              </HealthInformationCmsProvider>
            </I18nProvider>
          ) : IS_WEB ? (
            <View style={styles.webBoot}>
              <Text style={styles.webBootText}>Loading…</Text>
            </View>
          ) : splash_visible ? (
            <AppSplash
              can_dismiss={can_dismiss_splash}
              on_exit_complete={() => set_splash_visible(false)}
            />
          ) : null}
        </SafeAreaProvider>
      </QueryClientProvider>
    </GestureHandlerRootView>
  );
};

export default App;
